import { useState } from "react";
import { useMutation } from "react-query";
import { apiService, extractApiError } from "../services/api";
import { LicenseRequest } from "../types";

const TIER_OPTIONS: number[] = Array.from(
  { length: 20 },
  (_, i) => (i + 1) * 50,
);

const LICENSE_CHECKBOXES: { key: keyof LicenseTypeState; label: string }[] = [
  { key: "chat", label: "Chat" },
  { key: "cowork", label: "Cowork" },
  { key: "code", label: "Code" },
  { key: "design", label: "Design" },
];

interface LicenseTypeState {
  chat: boolean;
  cowork: boolean;
  code: boolean;
  design: boolean;
}

interface LicenseRequestFormProps {
  companyLabel: string;
}

export default function LicenseRequestForm({
  companyLabel,
}: LicenseRequestFormProps) {
  const [requestedTier, setRequestedTier] = useState<string>("");
  const [licenseTypes, setLicenseTypes] = useState<LicenseTypeState>({
    chat: false,
    cowork: false,
    code: false,
    design: false,
  });
  const [submitted, setSubmitted] = useState<LicenseRequest | null>(null);

  const mutation = useMutation(
    () => {
      const tier = parseInt(requestedTier, 10);
      return apiService.submitLicenseRequest(
        tier,
        "claude-enterprise",
        licenseTypes.code,
        licenseTypes.chat,
        licenseTypes.design,
        licenseTypes.cowork,
      );
    },
    {
      onSuccess: (res) => {
        if (res.data?.success && res.data.data) {
          setSubmitted(res.data.data as LicenseRequest);
        }
      },
    },
  );

  const toggleLicenseType = (key: keyof LicenseTypeState) => {
    setLicenseTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const anySelected =
    licenseTypes.chat ||
    licenseTypes.cowork ||
    licenseTypes.code ||
    licenseTypes.design;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestedTier || !anySelected) return;
    setSubmitted(null);
    mutation.mutate();
  };

  const errorMessage = mutation.isError
    ? extractApiError(mutation.error)
    : null;
  const canSubmit = !!requestedTier && anySelected;

  return (
    <div className="card">
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
        Pre-order a Claude Enterprise license for{" "}
        <span className="font-semibold">{companyLabel}</span>. Select the
        monthly tier that best fits your team.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="requestedTier"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Monthly usage limit (USD) - We will only chargeback based on usage,
            this is simply the limit you can use before you require additional
            approval.
          </label>
          <select
            id="requestedTier"
            value={requestedTier}
            onChange={(e) => setRequestedTier(e.target.value)}
            className="input"
            disabled={mutation.isLoading}
            required
          >
            <option value="">Select a tier</option>
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={String(t)}>
                ${t} / month
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            License type <span className="text-red-500">*</span>
          </span>
          <div className="flex flex-wrap gap-3">
            {LICENSE_CHECKBOXES.map(({ key, label }) => (
              <label
                key={key}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={licenseTypes[key]}
                  onChange={() => toggleLicenseType(key)}
                  disabled={mutation.isLoading}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {errorMessage && (
          <div role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errorMessage}
          </div>
        )}

        {submitted && !mutation.isError && (
          <div
            role="status"
            className="text-sm text-green-700 dark:text-green-400"
          >
            Submitted: ${submitted.requestedTier}/month for {submitted.resource}{" "}
            (status: {submitted.status}).
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={mutation.isLoading || !canSubmit}
        >
          {mutation.isLoading ? "Submitting..." : "Submit request"}
        </button>
      </form>
    </div>
  );
}
